// shared download path for release installers.
//
// preferred path (downloadRelease): ask the server for a presigned R2 URL
// and hand it to the browser — a normal native download (browser progress,
// resume, direct-to-disk). available once a release has been assembled into
// R2 (releases.r2_object_key stamped).
//
// fallback path (downloadReleaseAssembled): for the not-yet-migrated
// releases, fetch chunks individually, concatenate client-side, decompress
// if gzipped, and trigger a save dialog. each chunk is one ~131 KB request
// that completes in well under a second; a single streamed response from
// /api/releases/:id/download reliably fails for 100MB+ files because
// Cloudflare Free has a practical response-time ceiling around 100s that
// gets blown by slower client connections.

import pako from 'pako'
import { supabase as api } from '@/lib/supabase'
import { request as apiRequest } from '@/lib/apiClient'

export interface ReleaseDownloadShape {
  id: number
  installer_file_name: string
  installer_file_path?: string | null
  is_compressed: boolean | null
  is_split: boolean | null
  total_chunks: number | null
}

export type ReleaseDownloadMode = 'r2' | 'chunks'

interface DownloadUrlResponse {
  mode: ReleaseDownloadMode
  url?: string
  filename?: string
  bytes?: number
}

// entry point for the download buttons. resolves the release's download mode
// server-side, then either hands the presigned R2 URL to the browser (native
// download — no fetch loop, no pako, no JS progress) or runs the existing
// chunk-assembly fallback byte-for-byte unchanged. onMode fires as soon as
// the mode is known so callers can adapt their UI (the chunk path is the
// only one with a JS progress bar).
export async function downloadRelease(
  release: ReleaseDownloadShape,
  onProgress?: (loaded: number, total: number) => void,
  onMode?: (mode: ReleaseDownloadMode) => void,
): Promise<ReleaseDownloadMode> {
  const { data, error } = await apiRequest<DownloadUrlResponse>(
    'GET', `/api/releases/${release.id}/download-url`,
  )
  if (error) throw new Error(error.message)
  if (!data?.mode) throw new Error('Malformed download-url response')

  onMode?.(data.mode)

  if (data.mode === 'r2') {
    if (!data.url) throw new Error('download-url returned mode r2 without a url')
    // native browser download. the presigned URL carries
    // Content-Disposition: attachment with the real installer name, so the
    // browser saves it directly; the download attribute is a same-origin
    // nicety only (ignored cross-origin) but harmless
    const link = document.createElement('a')
    link.href = data.url
    link.download = data.filename ?? release.installer_file_name
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    return 'r2'
  }

  await downloadReleaseAssembled(release, onProgress)
  return 'chunks'
}

const BUCKET = 'releases'
const MAX_ATTEMPTS = 5
const BACKOFF_MS = [500, 1000, 2000, 4000, 8000]
// back to pair-wise after 6 caused HTTP/2 stream resets on flaky
// connections: when one chunk's response broke mid-stream, all
// six in-flight streams died together. per-chunk retries still
// apply (MAX_ATTEMPTS inside fetchChunk).
const BATCH_SIZE = 2

async function fetchChunk(
  chunk: { chunk_index: number; chunk_file_path: string },
): Promise<{ index: number; blob: Blob }> {
  let lastErr: unknown
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    try {
      const { data, error } = await api.storage.from(BUCKET).createSignedUrl(chunk.chunk_file_path, 3600)
      if (error || !data) throw error ?? new Error('Failed to sign release chunk')
      const response = await fetch(data.signedUrl)
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const blob = await response.blob()
      return { index: chunk.chunk_index, blob }
    } catch (err) {
      lastErr = err
      if (attempt < MAX_ATTEMPTS - 1) {
        await new Promise(r => setTimeout(r, BACKOFF_MS[attempt]))
      }
    }
  }
  throw new Error(`Chunk ${chunk.chunk_index} failed after ${MAX_ATTEMPTS} attempts: ${(lastErr as Error)?.message ?? 'unknown'}`)
}

export async function downloadReleaseAssembled(
  release: ReleaseDownloadShape,
  onProgress?: (loaded: number, total: number) => void,
): Promise<void> {
  let fileToDownload: Blob

  if (release.is_split && release.total_chunks) {
    const { data: chunksData, error: chunksError } = await api
      .from('release_file_chunks')
      .select('*')
      .eq('release_id', release.id)
      .order('chunk_index', { ascending: true })

    if (chunksError) throw chunksError
    if (!chunksData || chunksData.length !== release.total_chunks) {
      throw new Error('Not all chunks found')
    }

    onProgress?.(0, chunksData.length)

    const chunkResults: { index: number; blob: Blob }[] = []
    for (let i = 0; i < chunksData.length; i += BATCH_SIZE) {
      const batch = chunksData.slice(i, i + BATCH_SIZE)
      const batchResults = await Promise.all(batch.map(fetchChunk))
      chunkResults.push(...batchResults)
      onProgress?.(chunkResults.length, chunksData.length)
    }
    chunkResults.sort((a, b) => a.index - b.index)

    const chunkBuffers = await Promise.all(chunkResults.map(r => r.blob.arrayBuffer()))
    const totalSize = chunkBuffers.reduce((sum, buf) => sum + buf.byteLength, 0)
    const combined = new Uint8Array(totalSize)
    let offset = 0
    for (const buffer of chunkBuffers) {
      combined.set(new Uint8Array(buffer), offset)
      offset += buffer.byteLength
    }

    fileToDownload = new Blob([combined], { type: 'application/gzip' })
  } else {
    if (!release.installer_file_path) throw new Error('Release installer path is missing')
    const { data, error } = await api.storage.from(BUCKET).createSignedUrl(release.installer_file_path, 3600)
    if (error || !data) throw error ?? new Error('Failed to sign release installer')
    const response = await fetch(data.signedUrl)
    if (!response.ok) throw new Error('Failed to download file')
    fileToDownload = await response.blob()
  }

  if (release.is_compressed) {
    // yield once so the spinner can repaint before the synchronous
    // ungzip call blocks the main thread on large payloads
    await new Promise(resolve => setTimeout(resolve, 0))
    const ab = await fileToDownload.arrayBuffer()
    const decompressed = pako.ungzip(new Uint8Array(ab))
    fileToDownload = new Blob([decompressed], { type: 'application/x-msdownload' })
  }

  const url = URL.createObjectURL(fileToDownload)
  const link = document.createElement('a')
  link.href = url
  link.download = release.installer_file_name
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
